/* eslint-disable max-len */
import React from 'react';

import classNames from 'classnames';

import FeedbackIcon from './FeedbackIcon';

import { SatCoreRegister } from '../SatCoreRegistry';

import { getStyleVar, makeKeyboardAccessible } from '../utils';

import { register } from '../i18n';

const t = register('FeedbackIconSvg');

/**
 * FeedbackIconSvg
 *
 * A robust variant of FeedbackIcon that renders the active icon inline as
 * SVG so its colors can be themed via CSS variables (or per-call props).
 * Use this on new surfaces where the icon color should be driven by the
 * satellite's CSS, without producing a static SVG file per color.
 *
 *   - active background shape color: defaults to var(--icon-feedback-active-default-color)
 *   - active inner dots color: defaults to var(--icon-feedback-active-default-dot-color) (or #fff)
 *
 * Both can be overridden per-instance via the iconBackgroundColor / iconDotColor props.
 *
 * When `hasFeedback` is true (default), renders the active two-path SVG inline;
 * when false, delegates to the existing FeedbackIcon (which already handles
 * the inactive state via the original icon-comment-none.svg).
 *
 * Existing surfaces continue to use the original FeedbackIcon (file-per-hex pattern).
 */
const FeedbackIconSvg = (props = {}) => {
  const { className, clickHandler, disabled, hasFeedback = true, tabIndex } = props;

  const iconBackgroundColor = props.iconBackgroundColor || getStyleVar('--icon-feedback-active-default-color');
  const iconDotColor = props.iconDotColor || getStyleVar('--icon-feedback-active-default-dot-color');

  const keyboardProps = clickHandler
    ? makeKeyboardAccessible(clickHandler, { disabled })
    : {};

  const renderActiveFeedbackIcon = () => {
    return (
      <span aria-label={t('ariaFeedbackIcon')}
        className={classNames('FeedbackIconSvg', 'clickable', className, { disabled })}
        onClick={!disabled ? clickHandler : undefined}
        role={clickHandler ? 'button' : undefined}
        tabIndex={tabIndex}
        {...keyboardProps}>
        <svg height='26.407' viewBox='0 0 30.184 26.407' width='30.184' xmlns='http://www.w3.org/2000/svg'>
          <path d='M8.426 42.374a1.886 1.886 0 1 0 1.886 1.886 1.884 1.884 0 0 0-1.886-1.886zm6.6 0a1.886 1.886 0 1 0 1.886 1.886 1.884 1.884 0 0 0-1.885-1.886zm6.6 0a1.886 1.886 0 1 0 1.886 1.886 1.884 1.884 0 0 0-1.883-1.886zM15.027 32C6.693 32-.062 37.488-.062 44.261a10.836 10.836 0 0 0 3.118 7.445A14.958 14.958 0 0 1 .321 56.02a1.415 1.415 0 0 0 1.032 2.387 14.3 14.3 0 0 0 8.2-2.729 18.2 18.2 0 0 0 5.476.843c8.335 0 15.09-5.488 15.09-12.261S23.362 32 15.027 32z'
            style={{ fill: iconBackgroundColor }} transform='translate(.066 -32)' />
          <path d='M8.426 42.374a1.886 1.886 0 1 0 1.886 1.886 1.884 1.884 0 0 0-1.886-1.886zm6.6 0a1.886 1.886 0 1 0 1.886 1.886 1.884 1.884 0 0 0-1.885-1.886zm6.6 0a1.886 1.886 0 1 0 1.886 1.886 1.884 1.884 0 0 0-1.883-1.886z'
            data-name='icon_comment' style={{ fill: iconDotColor }} transform='translate(.066 -32)' />
        </svg>
      </span>
    );
  };

  const renderInactiveFeedbackIcon = () => {
    return (
      <FeedbackIcon
        className={className}
        clickHandler={clickHandler}
        disabled={disabled}
        hasFeedback={false}
        tabIndex={tabIndex} />
    );
  };

  return hasFeedback ? renderActiveFeedbackIcon() : renderInactiveFeedbackIcon();
};

export default FeedbackIconSvg;

SatCoreRegister('FeedbackIconSvg', FeedbackIconSvg);
